import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getSeasonGames, getSeasonStandings } from "../api/client";
import { SkeletonBlock, SkeletonBar } from "./Skeleton";

function seasonTitle(slug) {
  const match = String(slug || "").match(/(\d+)/);
  return match ? `Season ${match[1]}` : "Season";
}

function statusLabel(status) {
  if (status === "final" || status === "finished") return "Final";
  if (status === "live") return "Live";
  return "Scheduled";
}

function statusClasses(status) {
  if (status === "final" || status === "finished") {
    return "bg-[rgba(52,211,153,0.12)] text-[#34d399] border-[#34d399]";
  }
  if (status === "live") {
    return "bg-[rgba(248,113,113,0.12)] text-[#f87171] border-[#f87171]";
  }
  return "bg-[#ffffff] text-[#64748b] border-[#e2e8f0]";
}

export default function Schedule() {
  const { season } = useParams();
  const activeSeason = season || "szn5";

  const [games, setGames] = useState([]);
  const [records, setRecords] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const controller = new AbortController();

    setLoading(true);
    setError("");

    Promise.all([
      getSeasonGames(activeSeason, { signal: controller.signal }),
      getSeasonStandings(activeSeason, { signal: controller.signal }),
    ])
      .then(([gamesData, standingsData]) => {
        const nextGames = Array.isArray(gamesData)
          ? gamesData
          : gamesData?.games || [];
        const nextRecords = (standingsData?.standings || []).reduce(
          (acc, row) => {
            const teamName = row.team || row.name;
            if (teamName) {
              acc[teamName] = {
                wins: row.wins || 0,
                losses: row.losses || 0,
              };
            }
            return acc;
          },
          {}
        );

        setGames(nextGames);
        setRecords(nextRecords);
        setLoading(false);
      })
      .catch((err) => {
        if (err.name === "AbortError") return;
        console.error("Error loading schedule:", err);
        setGames([]);
        setRecords({});
        setError("Unable to load the schedule. Please try again later.");
        setLoading(false);
      });

    return () => controller.abort();
  }, [activeSeason]);

  const playoffGames = games.filter((game) => game.isPlayoff);
  const regularGames = games.filter((game) => !game.isPlayoff);

  const gamesByDate = regularGames.reduce((acc, game) => {
    if (!acc[game.date]) acc[game.date] = [];
    acc[game.date].push(game);
    return acc;
  }, {});
  const dateEntries = Object.entries(gamesByDate).sort(
    ([a], [b]) => new Date(a) - new Date(b)
  );

  const playoffDate = playoffGames.find((game) => game.date)?.date;

  function renderGameCard(game, idx) {
    const [weekPart, idPart] =
      typeof game.gameId === "string" ? game.gameId.split("-") : ["", ""];
    const isFinished = game.status === "final" || game.status === "finished";
    const isLive = game.status === "live";
    const teamAWon = isFinished && game.scoreA > game.scoreB;
    const teamBWon = isFinished && game.scoreB > game.scoreA;
    const recA = records[game.teamA] || { wins: 0, losses: 0 };
    const recB = records[game.teamB] || { wins: 0, losses: 0 };
    const hasScore =
      (isFinished || isLive) &&
      typeof game.scoreA === "number" &&
      typeof game.scoreB === "number";

    const cardClasses = `h-full rounded-lg border bg-[#f8fafc] p-4 shadow-sm transition ${
      isFinished
        ? "border-[#e2e8f0] hover:border-[#0284c7] hover:shadow-md"
        : isLive
          ? "border-[#f87171] hover:border-[#f87171] hover:shadow-md"
          : "border-[#e2e8f0] opacity-80"
    }`;

    const teamRow = (name, isPlaceholder, rec, won, score) => (
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3">
        <div className="min-w-0">
          <div
            className={`min-w-0 truncate text-sm font-black ${
              won
                ? "text-[#34d399]"
                : isPlaceholder
                  ? "italic text-[#94a3b8]"
                  : "text-[#0f172a]"
            }`}
          >
            {name}
          </div>
          {!isPlaceholder && (
            <div className="mt-0.5 text-xs font-bold text-[#64748b]">
              {rec.wins}-{rec.losses}
            </div>
          )}
        </div>
        <div className="min-w-[2.25rem] text-right text-2xl font-black text-[#0f172a]">
          {score}
        </div>
      </div>
    );

    const content = (
      <article className={cardClasses}>
        <div className="mb-4 flex items-center justify-between gap-3">
          <span
            className={`rounded-full border px-2.5 py-1 text-[11px] font-black uppercase ${statusClasses(
              game.status
            )}`}
          >
            {statusLabel(game.status)}
          </span>
          <span className="text-xs font-bold text-[#64748b]">
            {game.time || "TBD"}
          </span>
        </div>

        <div className="grid gap-3">
          {teamRow(
            game.teamA,
            game.teamAIsPlaceholder,
            recA,
            teamAWon,
            hasScore ? game.scoreA : "-"
          )}

          <div className="h-px bg-[#0f172a]" />

          {teamRow(
            game.teamB,
            game.teamBIsPlaceholder,
            recB,
            teamBWon,
            hasScore ? game.scoreB : "-"
          )}
        </div>

        {isLive ? (
          <div className="mt-4 rounded-md bg-[rgba(248,113,113,0.1)] px-3 py-2 text-center text-xs font-black text-[#f87171]">
            Live now — Tap for play-by-play
          </div>
        ) : (
          !isFinished && (
            <div className="mt-4 rounded-md bg-[#ffffff] px-3 py-2 text-center text-xs font-black text-[#64748b]">
              Box score available after game
            </div>
          )
        )}
      </article>
    );

    return (isFinished || isLive) && weekPart && idPart ? (
      <Link
        key={idx}
        to={`/season/${activeSeason}/boxscore/${weekPart}/${idPart}`}
        className="block no-underline"
      >
        {content}
      </Link>
    ) : (
      <div key={idx}>{content}</div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc] px-4 py-8 text-[#0f172a] sm:px-6">
      <div className="mx-auto max-w-5xl">
        <header className="mb-8">
          <p className="mb-2 text-center text-xs font-black uppercase tracking-[0.18em] text-[#0284c7]">
            {seasonTitle(activeSeason)}
          </p>
          <h2 className="text-center text-3xl font-black tracking-tight text-[#0f172a] sm:text-4xl">
            Schedule
          </h2>
        </header>

        {loading ? (
          <div className="grid gap-6">
            {Array.from({ length: 2 }).map((_, dayIdx) => (
              <SkeletonBlock
                key={dayIdx}
                className="overflow-hidden rounded-lg border border-[#e2e8f0] bg-[#ffffff] shadow-sm"
              >
                <div className="flex items-center justify-between gap-3 border-b border-[#e2e8f0] px-4 py-3 sm:px-5">
                  <div className="flex flex-col gap-1.5">
                    <SkeletonBar className="h-3 w-16" />
                    <SkeletonBar className="h-5 w-40" />
                  </div>
                  <SkeletonBar className="h-6 w-16 rounded-full" />
                </div>
                <div className="grid gap-3 p-4 sm:grid-cols-2 sm:p-5 lg:grid-cols-3">
                  {Array.from({ length: 3 }).map((__, cardIdx) => (
                    <div
                      key={cardIdx}
                      className="rounded-lg border border-[#e2e8f0] bg-[#f8fafc] p-4 shadow-sm"
                    >
                      <div className="mb-4 flex items-center justify-between">
                        <SkeletonBar className="h-5 w-16 rounded-full" />
                        <SkeletonBar className="h-4 w-10" />
                      </div>
                      <div className="grid gap-3">
                        <div className="flex items-center justify-between">
                          <SkeletonBar className="h-4 w-24" />
                          <SkeletonBar className="h-6 w-6" />
                        </div>
                        <div className="h-px bg-[#e2e8f0]" />
                        <div className="flex items-center justify-between">
                          <SkeletonBar className="h-4 w-24" />
                          <SkeletonBar className="h-6 w-6" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </SkeletonBlock>
            ))}
          </div>
        ) : error ? (
          <div className="rounded-lg border border-[#f87171] bg-[rgba(248,113,113,0.1)] p-8 text-center font-bold text-[#f87171]">
            {error}
          </div>
        ) : (
          <div className="ifn-fade-in grid gap-6">
            {playoffGames.length > 0 && (
              <section className="overflow-hidden rounded-lg border-2 border-[#f59e0b] bg-[#ffffff] shadow-sm">
                <div className="flex items-center justify-between gap-3 border-b border-[#e2e8f0] bg-[rgba(245,158,11,0.08)] px-4 py-3 sm:px-5">
                  <div>
                    <p className="text-xs font-black uppercase tracking-[0.14em] text-[#b45309]">
                      Playoffs{playoffDate ? ` · ${playoffDate}` : ""}
                    </p>
                    <h3 className="text-lg font-black text-[#0f172a]">
                      Championship Bracket
                    </h3>
                  </div>
                  <span className="rounded-full bg-[rgba(245,158,11,0.16)] px-3 py-1 text-xs font-black text-[#b45309]">
                    {playoffGames.length}{" "}
                    {playoffGames.length === 1 ? "game" : "games"}
                  </span>
                </div>

                <div className="grid gap-3 p-4 sm:grid-cols-2 sm:p-5 lg:grid-cols-3">
                  {playoffGames.map((game, idx) => renderGameCard(game, idx))}
                </div>
              </section>
            )}

            {dateEntries.map(([date, dayGames]) => {
              return (
                <section
                  key={date}
                  className="overflow-hidden rounded-lg border border-[#e2e8f0] bg-[#ffffff] shadow-sm"
                >
                  <div className="flex items-center justify-between gap-3 border-b border-[#e2e8f0] px-4 py-3 sm:px-5">
                    <div>
                      <p className="text-xs font-black uppercase tracking-[0.14em] text-[#64748b]">
                        Game Day
                      </p>
                      <h3 className="text-lg font-black text-[#0f172a]">
                        {date || "Date TBD"}
                      </h3>
                    </div>
                    <span className="rounded-full bg-[rgba(56,189,248,0.12)] px-3 py-1 text-xs font-black text-[#0284c7]">
                      {dayGames.length} {dayGames.length === 1 ? "game" : "games"}
                    </span>
                  </div>

                  <div className="grid gap-3 p-4 sm:grid-cols-2 sm:p-5 lg:grid-cols-3">
                    {dayGames.map((game, idx) => renderGameCard(game, idx))}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
